import { useCallback, useEffect, useRef, useState } from 'react'
import type { AxiosError } from 'axios'

import { useApiClient } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { logger } from '../../helpers/logger.helper'
import { AGE_GATE_API_CODES } from '../constants/ageGate.constants'
import { validateDateOfBirthParts, type DateOfBirthParts } from '../helpers/dateOfBirth.helper'
import { useAgeGateStore, type AgeGateStatus } from '../stores/ageGateStore'

interface PrivacyPreferencesAgeResponse {
  data?: { ageVerified?: boolean; ageBlocked?: boolean }
}

export type AgeGateSubmitError = 'invalid' | 'failed' | null

export interface UseAgeVerificationResult {
  status: AgeGateStatus
  isSubmitting: boolean
  submitError: AgeGateSubmitError
  submit: (parts: DateOfBirthParts) => Promise<void>
  signOut: () => Promise<void>
}

function responseCode(error: unknown): string | undefined {
  return (error as AxiosError<{ code?: string }>)?.response?.data?.code
}

/**
 * Resolves and updates the signed-in user's age-gate status (specs/coin-compliance).
 *
 * The server's record is the only source: a blocked account stays blocked, and signing out
 * and starting again as a new guest simply asks again. There is deliberately no device-level
 * lock — the gate exists to be seen and to refuse an under-18 answer, not to trap anyone.
 */
export function useAgeVerification(): UseAgeVerificationResult {
  const apiClient = useApiClient()
  const apiClientRef = useRef(apiClient)
  const { isLoaded, isSignedIn, userId, signOut } = useSupabaseAuth()
  const status = useAgeGateStore((s) => s.status)
  const setStatus = useAgeGateStore((s) => s.setStatus)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<AgeGateSubmitError>(null)

  useEffect(() => {
    apiClientRef.current = apiClient
  }, [apiClient])

  const resolveStatus = useCallback(
    async (forUserId: string): Promise<void> => {
      const client = apiClientRef.current
      if (!client) return
      try {
        const response = await client<PrivacyPreferencesAgeResponse>(
          API_ENDPOINTS.users.privacyPreferences
        )
        const data = response.data?.data
        if (data?.ageBlocked) {
          setStatus('blocked', forUserId)
        } else if (data?.ageVerified) {
          setStatus('verified', forUserId)
        } else {
          setStatus('required', forUserId)
        }
      } catch (error) {
        // A failed status check must never lock anyone out; try again next app open.
        logger.warn('[ageGate] status check failed, not blocking', { error })
        setStatus('unknown', forUserId)
      }
    },
    [setStatus]
  )

  useEffect(() => {
    if (!isLoaded) return
    if (!isSignedIn || !userId) {
      useAgeGateStore.getState().reset()
      return
    }
    const current = useAgeGateStore.getState()
    if (current.userId === userId && (current.status === 'verified' || current.status === 'blocked')) {
      return
    }
    void resolveStatus(userId)
  }, [isLoaded, isSignedIn, userId, resolveStatus])

  const submit = useCallback(
    async (parts: DateOfBirthParts) => {
      const validation = validateDateOfBirthParts(parts)
      if (!validation.ok) {
        setSubmitError('invalid')
        return
      }
      const client = apiClientRef.current
      if (!client || !userId) return

      setIsSubmitting(true)
      setSubmitError(null)
      try {
        await client(API_ENDPOINTS.users.dateOfBirth, {
          method: 'POST',
          data: { birthDate: validation.isoDate },
        })
        setStatus('verified', userId)
      } catch (error) {
        const code = responseCode(error)
        if (code === AGE_GATE_API_CODES.INELIGIBLE) {
          setStatus('blocked', userId)
        } else if (code === AGE_GATE_API_CODES.BIRTH_DATE_ALREADY_SET) {
          // Recorded elsewhere (another device or a double tap): adopt the server's answer.
          await resolveStatus(userId)
        } else if (code === AGE_GATE_API_CODES.INVALID_BIRTH_DATE) {
          setSubmitError('invalid')
        } else {
          setSubmitError('failed')
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [userId, setStatus, resolveStatus]
  )

  return { status, isSubmitting, submitError, submit, signOut }
}
